import React, { useState, useEffect, ReactNode, ErrorInfo } from 'react';
import Layout from './components/Layout';
import SurveyForm from './components/SurveyForm';
import Dashboard from './components/Dashboard';
import CardGenerator from './components/CardGenerator';
import AdminManager from './components/AdminManager';
import MySubmissions from './components/MySubmissions';
import QuestionManager from './components/QuestionManager';
import AdvancedSearch from './components/AdvancedSearch';
import Reports from './components/Reports';
import { ToastProvider } from './components/Toast';
import { User, UserRole } from './types';
import { initStorage, loginUser, logoutUser, registerMasterAdmin, getEmail, createUserProfile } from './services/storage';
import { auth } from './services/firebase';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Error Boundary Component
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState;
  public props: ErrorBoundaryProps;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.props = props;
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-lg w-full">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">The application encountered an unexpected error.</p>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto mb-4 border border-gray-300">
              {this.state.error?.message}
            </pre>
            <button 
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inline Login Component
const LoginView = ({ 
  onLogin
}: { 
  onLogin: (u: string, p: string) => Promise<boolean>
}) => {
  const [username, setUsername] = useState('shabeebkk10@gmail.com');
  const [password, setPassword] = useState('shabeeb123');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Trim inputs to prevent accidental spaces causing issues
      const success = await onLogin(username.trim(), password);
      if (!success) setError('Invalid credentials');
    } catch (err: any) {
      // Show cleaner error messages
      const msg = err.message || 'Action failed';
      if (msg.includes('auth/invalid-credential') || msg.includes('invalid-credential')) {
        setError('Invalid username or password. Please try again.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-800 to-teal-900 flex items-center justify-center p-4 relative">
      <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
        <h1 className="text-4xl font-bold text-center text-emerald-800 mb-2">മഹല്ല് സർവ്വേ</h1>
        <p className="text-center text-emerald-600 font-bold mb-8">കാമിച്ചേരി ജുമാ മസ്ജിദ് , കാമിച്ചേരി, കടമേരി</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center mb-2">
            <h2 className="text-xl font-bold text-gray-700">Admin Login</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Username or Email</label>
            <input 
              type="text" 
              className="mt-1 w-full border border-gray-300 rounded-md p-3 focus:ring-emerald-500 focus:border-emerald-500" 
              placeholder="Enter username or email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input 
              type="password" 
              className="mt-1 w-full border border-gray-300 rounded-md p-3 focus:ring-emerald-500 focus:border-emerald-500" 
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded text-sm break-words">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading}
            className={`w-full bg-emerald-600 text-white py-3 rounded-md hover:bg-emerald-700 transition font-bold shadow-md ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Processing...' : 'Login'}
          </button>
        </form>
      </div>

      {/* Developer Badge */}
      <div className="fixed bottom-4 right-4 z-50">
        <span className="bg-emerald-200 text-emerald-900 px-4 py-2 rounded-lg font-bold shadow-lg border-2 border-emerald-400 text-sm">
          Developed by Shabeeb Kayakkandy
        </span>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [currentView, setCurrentView] = useState('dashboard');

  // Removed initStorage from useEffect because it writes to DB and requires auth.
  // It is now called in handleLogin.

  const handleLogin = async (u: string, p: string) => {
    try {
      const foundUser = await loginUser(u, p);
      if (foundUser) {
        setUser(foundUser);
        setCurrentView(foundUser.role === UserRole.REPRESENTATIVE ? 'survey' : 'dashboard');
        
        // Initialize storage after successful login (ensures permissions)
        initStorage().catch(err => console.warn("Init storage background check failed:", err));
        
        return true;
      }
      return false;
    } catch (e: any) {
      // Auto-bootstrap
      const targetEmail = 'shabeebkk10@gmail.com';
      const computedEmail = getEmail(u);

      if (computedEmail === targetEmail) {
         // Check if we are actually logged in to Auth but just missing the Firestore doc
         const currentUser = auth.currentUser;
         if (currentUser && currentUser.email === targetEmail) {
           try {
             console.log("Found Master Admin Auth session but missing profile. Creating profile...");
             const newUser = await createUserProfile(currentUser.uid, u, 'Mahallu Admin', UserRole.MASTER_ADMIN);
             setUser(newUser);
             setCurrentView('dashboard');
             initStorage().catch(console.error);
             return true;
           } catch (profileErr) {
             console.error("Failed to recover Master Admin profile:", profileErr);
             throw new Error("Profile creation failed. Contact developer.");
           }
         }

         // If we get here, either we weren't signed in or it's a first time registration
         try {
           // Only try registration if loginUser failed (which it did to get here)
           // But if it was an "Invalid credentials" error from loginUser, we should distinguish.
           // However, for the Master Admin, we want to be helpful.
           
           console.log("Attempting to bootstrap Master Admin...");
           const newUser = await registerMasterAdmin('Mahallu Admin', targetEmail, p);
           setUser(newUser);
           setCurrentView('dashboard');
           
           // Init storage for master admin
           initStorage().catch(console.error);
           
           return true;
         } catch (regError: any) {
           // If registration fails because it already exists, then it's definitely a WRONG PASSWORD
           // because loginUser already failed with this email.
           if (regError.message && (regError.message.includes('already exists') || regError.message.includes('email-already-in-use'))) {
              throw new Error("Invalid username or password. Please check your credentials.");
           }
           console.error("Bootstrap failed:", regError);
           throw regError;
         }
      }
      throw e;
    }
  };

  const handleLogout = async () => {
    await logoutUser();
    setUser(null);
  };

  const renderContent = () => {
    if (!user) return null;

    switch (currentView) {
      case 'dashboard':
        return user.role !== UserRole.REPRESENTATIVE ? <Dashboard user={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'search':
        return user.role !== UserRole.REPRESENTATIVE ? <AdvancedSearch /> : <div className="text-red-500">Access Denied</div>;
      case 'reports':
        return user.role !== UserRole.REPRESENTATIVE ? <Reports /> : <div className="text-red-500">Access Denied</div>;
      case 'survey':
        return <SurveyForm user={user} onSuccess={() => setCurrentView(user.role === UserRole.REPRESENTATIVE ? 'my-submissions' : 'dashboard')} />;
      case 'my-submissions':
        return user.role === UserRole.REPRESENTATIVE ? <MySubmissions user={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'card-gen':
        // RESTRICTED TO MASTER ADMIN
        return user.role === UserRole.MASTER_ADMIN ? <CardGenerator /> : <div className="p-8 text-center text-red-500 bg-red-50 border border-red-200 rounded-lg">Access Denied: Only Master Admin can access ID Cards.</div>;
      case 'admin-users':
        return user.role === UserRole.MASTER_ADMIN ? <AdminManager currentUser={user} /> : <div className="text-red-500">Access Denied</div>;
      case 'settings':
        // RESTRICTED TO MASTER ADMIN
        return user.role === UserRole.MASTER_ADMIN ? <QuestionManager /> : <div className="p-8 text-center text-red-500 bg-red-50 border border-red-200 rounded-lg">Access Denied: Only Master Admin can manage questions.</div>;
      default:
        return <div className="text-center text-gray-500 mt-10">Select an option from the menu</div>;
    }
  };

  if (!user) {
    return (
      <ErrorBoundary>
        <LoginView onLogin={handleLogin} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Layout user={user} onLogout={handleLogout} onNavigate={setCurrentView} currentView={currentView}>
          {renderContent()}
        </Layout>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;